import http from './client';
import { errorHandler, parseApiError } from './errorHandler';
import type { ApiResponse, User } from './types';

type UserProfile = User;

interface UpdateProfileData {
  name?: string;
  number?: string;
  country_code?: string;
  [key: string]: unknown;
}

/**
 * The backend's /users/me endpoint does not reliably return all user fields
 * (notably `avatar`). This helper merges the API response with the locally
 * cached user so those fields are never silently lost on any getProfile call.
 *
 * Rules:
 *  - If the API returns a field with a non-undefined value, that value wins.
 *  - If the API omits a field (undefined), the cached value is kept as fallback.
 *  - This is a pure data function — no side-effects on localStorage.
 */
function mergeWithCachedUser(apiUser: UserProfile): UserProfile {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return apiUser;
    const cached = JSON.parse(raw) as Partial<UserProfile>;
    // Merge: for every key present in `cached`, use the API value if defined,
    // otherwise fall back to the cached value.
    const merged = { ...apiUser };
    (Object.keys(cached) as (keyof UserProfile)[]).forEach((key) => {
      if (merged[key] === undefined && cached[key] !== undefined) {
        // TypeScript needs the cast because we can't infer the exact key type
        (merged as Record<string, unknown>)[key] = cached[key];
      }
    });
    return merged;
  } catch {
    return apiUser;
  }
}

export const userApi = {
  getProfile: async (): Promise<ApiResponse<UserProfile>> => {
    try {
      const response = await http.get('/users/me');
      const responseData = response.data as
        | { user?: UserProfile }
        | UserProfile;
      const raw = 'user' in responseData ? responseData.user : responseData;
      // Merge with cached user so fields the backend omits (e.g. avatar)
      // are never lost regardless of which component calls getProfile().
      return {
        success: true,
        data: mergeWithCachedUser(raw as UserProfile),
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  updateProfile: async (
    data: UpdateProfileData | FormData,
  ): Promise<ApiResponse<UserProfile>> => {
    try {
      const response = await http.put('/users/me', data);
      const res = response.data as { user?: UserProfile };
      return {
        success: true,
        data: (res?.user ?? response.data) as UserProfile,
      };
    } catch (error) {
      const { message, fieldErrors } = parseApiError(error);
      return {
        success: false,
        error: message,
        fieldErrors,
      };
    }
  },

  updateMedicalProfile: async (
    data: Record<string, unknown>,
  ): Promise<ApiResponse<UserProfile>> => {
    try {
      const response = await http.post('/profile/medical', data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  updateDoshaProfile: async (
    data: Record<string, unknown>,
  ): Promise<ApiResponse<UserProfile>> => {
    try {
      const response = await http.post('/profile/dosha', data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  updateBioHeadline: async (data: {
    bio: string;
    headline: string;
  }): Promise<ApiResponse<UserProfile>> => {
    try {
      const response = await http.put('/profile/bio-headline', data);
      return {
        success: true,
        data: response.data.user || response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },
};
